// Wenn REACT_APP_API_BASE nicht gesetzt ist, werden relative Pfade verwendet
// (z.B. "/api/..."). Der Browser sendet diese dann automatisch an die gleiche
// Domain, von der die Seite geladen wurde — das funktioniert sowohl lokal
// (via Nginx-Proxy zum Backend-Container) als auch hinter Pangolin/jedem
// anderen Reverse Proxy, ohne dass das Frontend eine fixe Backend-URL kennen muss.
const API_BASE = process.env.REACT_APP_API_BASE || "";

async function handleResponse(resp) {
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`API-Fehler (${resp.status}): ${text}`);
  }
  return resp.json();
}

export async function getThemenfelder() {
  const resp = await fetch(`${API_BASE}/api/filters/themenfelder`);
  return handleResponse(resp);
}

export async function getIndikatoren(themenfeld) {
  const url = new URL(`${API_BASE}/api/filters/indikatoren`, window.location.origin);
  if (themenfeld) url.searchParams.set("themenfeld", themenfeld);
  const resp = await fetch(url);
  return handleResponse(resp);
}

export async function getDatensaetze(themenfeld, indikator) {
  const url = new URL(`${API_BASE}/api/filters/datensaetze`, window.location.origin);
  if (themenfeld) url.searchParams.set("themenfeld", themenfeld);
  if (indikator) url.searchParams.set("indikator", indikator);
  const resp = await fetch(url);
  return handleResponse(resp);
}

export async function getGemeinden() {
  const resp = await fetch(`${API_BASE}/api/filters/gemeinden`);
  return handleResponse(resp);
}

export async function getQuelle(datensatz, bfsTableId) {
  const url = new URL(`${API_BASE}/api/quelle`, window.location.origin);
  if (datensatz) url.searchParams.set("datensatz", datensatz);
  if (bfsTableId) url.searchParams.set("bfs_table_id", bfsTableId);
  const resp = await fetch(url);
  return handleResponse(resp);
}

export async function smartSearch(query) {
  const resp = await fetch(`${API_BASE}/api/search/smart`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query }),
  });
  return handleResponse(resp);
}

export async function simpleSearch(query) {
  const resp = await fetch(`${API_BASE}/api/search/simple`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query }),
  });
  return handleResponse(resp);
}

export async function search(filters) {
  const resp = await fetch(`${API_BASE}/api/search`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(filters),
  });
  return handleResponse(resp);
}

export async function bfsQuery(tableId, years, prefer, filter1, filter2) {
  const resp = await fetch(`${API_BASE}/api/bfs/query`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      table_id: tableId,
      years: years || null,
      prefer: prefer || null,
      filter1_code: filter1?.code || null,
      filter1_values: filter1?.values || null,
      filter2_code: filter2?.code || null,
      filter2_values: filter2?.values || null,
    }),
  });
  return handleResponse(resp);
}

export async function bfsFilterDimensions(tableId) {
  const resp = await fetch(`${API_BASE}/api/bfs/filter-dimensions/${encodeURIComponent(tableId)}`);
  return handleResponse(resp);
}

export async function exportExcel(filters) {
  const resp = await fetch(`${API_BASE}/api/export/excel`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(filters),
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Export fehlgeschlagen (${resp.status}): ${text}`);
  }
  const blob = await resp.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "wifoe_export.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function exportExcelBfs(tableId, prefer, filter1, filter2) {
  const resp = await fetch(`${API_BASE}/api/export/excel/bfs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      table_id: tableId,
      prefer: prefer || null,
      filter1_code: filter1?.code || null,
      filter1_values: filter1?.values || null,
      filter2_code: filter2?.code || null,
      filter2_values: filter2?.values || null,
    }),
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`BFS-Export fehlgeschlagen (${resp.status}): ${text}`);
  }
  const blob = await resp.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "bfs_export.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function chatQuery(question, history) {
  const resp = await fetch(`${API_BASE}/api/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ question, history }),
  });
  return handleResponse(resp);
}

export async function getChatbotStatus() {
  const resp = await fetch(`${API_BASE}/api/chatbot-status`);
  return handleResponse(resp);
}

export async function adminLogin(password) {
  const resp = await fetch(`${API_BASE}/api/admin/login`, {
    method: "POST", headers: {"Content-Type":"application/json"},
    body: JSON.stringify({ password }),
  });
  return handleResponse(resp);
}

export async function adminLogs(password, n = 1000) {
  const resp = await fetch(`${API_BASE}/api/admin/logs?password=${encodeURIComponent(password)}&n=${n}`);
  return handleResponse(resp);
}

export async function adminTokens(password) {
  const resp = await fetch(`${API_BASE}/api/admin/tokens?password=${encodeURIComponent(password)}`);
  return handleResponse(resp);
}

export async function adminSetChatbot(password, enabled) {
  const resp = await fetch(`${API_BASE}/api/admin/chatbot`, {
    method: "POST", headers: {"Content-Type":"application/json"},
    body: JSON.stringify({ password, enabled }),
  });
  return handleResponse(resp);
}

export async function adminClearCache(password) {
  const resp = await fetch(`${API_BASE}/api/admin/clear-cache`, {
    method: "POST", headers: {"Content-Type":"application/json"},
    body: JSON.stringify({ password }),
  });
  return handleResponse(resp);
}

export async function getMe() {
  const resp = await fetch(`${API_BASE}/auth/me`);
  if (resp.status === 401) return null;
  return handleResponse(resp);
}

export async function logout() {
  await fetch(`${API_BASE}/auth/logout`, { method: "POST" });
}

// ---------- Persistent Chat API ----------
export async function getChats() {
  const resp = await fetch(`${API_BASE}/api/chats`);
  return handleResponse(resp);
}

export async function getChat(chatId) {
  const resp = await fetch(`${API_BASE}/api/chats/${chatId}`);
  return handleResponse(resp);
}

export async function createChat() {
  const resp = await fetch(`${API_BASE}/api/chats`, { method: "POST" });
  return handleResponse(resp);
}

export async function deleteChat(chatId) {
  const resp = await fetch(`${API_BASE}/api/chats/${chatId}`, { method: "DELETE" });
  return handleResponse(resp);
}

export async function renameChat(chatId, title) {
  const resp = await fetch(`${API_BASE}/api/chats/${chatId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title }),
  });
  return handleResponse(resp);
}

export async function sendChatMessage(chatId, question) {
  const resp = await fetch(`${API_BASE}/api/chats/${chatId}/message`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ question }),
  });
  return handleResponse(resp);
}

export async function getMyRole() {
  const resp = await fetch(`${API_BASE}/api/me/role`);
  return handleResponse(resp);
}

export async function downloadTemplate() {
  const resp = await fetch(`${API_BASE}/api/import/template`);
  if (!resp.ok) throw new Error("Fehler beim Download");
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "wifoe_import_vorlage.csv";
  a.click();
  URL.revokeObjectURL(url);
}

export async function importPreview(data) {
  const resp = await fetch(`${API_BASE}/api/import/preview`, {
    method: "POST", headers: {"Content-Type": "application/json"},
    body: JSON.stringify(data),
  });
  return handleResponse(resp);
}

export async function importRun(data) {
  const resp = await fetch(`${API_BASE}/api/import/run`, {
    method: "POST", headers: {"Content-Type": "application/json"},
    body: JSON.stringify(data),
  });
  return handleResponse(resp);
}

export async function adminGetRoles(password) {
  const resp = await fetch(`${API_BASE}/api/admin/roles?password=${encodeURIComponent(password)}`);
  return handleResponse(resp);
}

export async function adminSetRole(password, oid, email, name, role) {
  const resp = await fetch(`${API_BASE}/api/admin/roles`, {
    method: "POST", headers: {"Content-Type": "application/json"},
    body: JSON.stringify({ password, oid, email, name, role }),
  });
  return handleResponse(resp);
}

export async function adminGetImports(password) {
  const resp = await fetch(`${API_BASE}/api/admin/imports?password=${encodeURIComponent(password)}`);
  return handleResponse(resp);
}

export async function adminUndoImport(password, importId) {
  const resp = await fetch(`${API_BASE}/api/admin/imports/${importId}`, {
    method: "DELETE", headers: {"Content-Type": "application/json"},
    body: JSON.stringify({ password }),
  });
  return handleResponse(resp);
}

export async function getCardPrefs() {
  const resp = await fetch(`${API_BASE}/api/card-prefs`);
  return handleResponse(resp);
}

export async function setCardPref(cardKey, prefs) {
  const resp = await fetch(`${API_BASE}/api/card-prefs/${encodeURIComponent(cardKey)}`, {
    method: "POST", headers: {"Content-Type": "application/json"},
    body: JSON.stringify(prefs),
  });
  return handleResponse(resp);
}







export async function sdmxListStats() {
  const resp = await fetch(`${API_BASE}/api/sdmx/stats`);
  return handleResponse(resp);
}

export async function sdmxInspect(url) {
  const resp = await fetch(`${API_BASE}/api/sdmx/inspect`, {
    method: "POST", headers: {"Content-Type": "application/json"},
    body: JSON.stringify({ url }),
  });
  return handleResponse(resp);
}

export async function sdmxRegister(data) {
  const resp = await fetch(`${API_BASE}/api/sdmx/stats`, {
    method: "POST", headers: {"Content-Type": "application/json"},
    body: JSON.stringify(data),
  });
  return handleResponse(resp);
}

export async function sdmxDelete(statId) {
  const resp = await fetch(`${API_BASE}/api/sdmx/stats/${encodeURIComponent(statId)}`, {
    method: "DELETE",
  });
  return handleResponse(resp);
}

export async function sdmxFilterDimensions(statId) {
  const resp = await fetch(`${API_BASE}/api/sdmx/filter-dimensions/${encodeURIComponent(statId)}`);
  return handleResponse(resp);
}

export async function sdmxQuery(statId, selection, startPeriod, endPeriod) {
  const resp = await fetch(`${API_BASE}/api/sdmx/query`, {
    method: "POST", headers: {"Content-Type": "application/json"},
    body: JSON.stringify({ stat_id: statId, selection, start_period: startPeriod, end_period: endPeriod }),
  });
  return handleResponse(resp);
}

export async function exportExcelSdmx(statId, selection, startPeriod, endPeriod) {
  const resp = await fetch(`${API_BASE}/api/export/excel/sdmx`, {
    method: "POST", headers: {"Content-Type": "application/json"},
    body: JSON.stringify({ stat_id: statId, selection, start_period: startPeriod, end_period: endPeriod }),
  });
  if (!resp.ok) throw new Error("Export fehlgeschlagen");
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "statistik.xlsx"; a.click();
  URL.revokeObjectURL(url);
}

export async function datensatzDeletePreview(payload) {
  const resp = await fetch(`${API_BASE}/api/datensatz/delete-preview`, {
    method: "POST", headers: {"Content-Type": "application/json"},
    body: JSON.stringify(payload),
  });
  return handleResponse(resp);
}

export async function datensatzDelete(payload) {
  const resp = await fetch(`${API_BASE}/api/datensatz/delete`, {
    method: "POST", headers: {"Content-Type": "application/json"},
    body: JSON.stringify(payload),
  });
  return handleResponse(resp);
}
